import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  resolveUrl as resolveUrlUtil,
  translateStoreCategory,
  localizedStoreProductName,
  localizedStoreProductDescription,
  isStoreArabicLang,
  isStoreProductOutOfStock,
  getStoreProductStockCap,
} from "../../../utils/storeUtils";
import { StoreProductPriceBlock } from "./StoreProductPriceBlock";
import SimpleMarkdown from "./SimpleMarkdown";

function QuickViewModal({
  product,
  isOpen,
  onClose,
  onAddToCart,
  resolveUrl: resolveUrlProp,
}) {
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const [quantity, setQuantity] = useState(1);
  const [fullProduct, setFullProduct] = useState(product);
  const [selectedColor, setSelectedColor] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const apiBaseUrl = axios.defaults.baseURL || "";
  const resolveUrl = useCallback(
    (url) => (resolveUrlProp ? resolveUrlProp(url) : resolveUrlUtil(url, apiBaseUrl)),
    [resolveUrlProp, apiBaseUrl]
  );

  useEffect(() => {
    if (!isOpen || !product?._id) return;
    setQuantity(1);
    setSelectedColor(null);
    setGalleryIndex(0);
    setFullProduct(product);
    setLoading(true);
    axios
      .get(`/store/products/${product._id}`)
      .then(({ data }) => setFullProduct(data))
      .catch(() => setFullProduct(product))
      .finally(() => setLoading(false));
  }, [isOpen, product]);

  useEffect(() => {
    if (!isOpen) return;
    const firstColor = fullProduct?.colors?.[0]?.name || null;
    setSelectedColor(firstColor);
  }, [fullProduct, isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const handleAddToCart = () => {
    if (!fullProduct) return;
    if (isStoreProductOutOfStock(fullProduct)) return;
    const cap = getStoreProductStockCap(fullProduct);
    if (cap !== null && quantity > cap) {
      toast.error(t("store.stockNotEnough", { defaultValue: "Not enough stock available" }));
      return;
    }
    const effectiveColor = (fullProduct?.colors || []).find(
      (c) => c?.name === selectedColor
    );
    for (let i = 0; i < quantity; i++) {
      onAddToCart(fullProduct, {
        selectedColor: selectedColor || undefined,
        selectedColorHex: effectiveColor?.hexes?.[0] || effectiveColor?.hex || undefined,
      });
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  const images = fullProduct?.images?.length
    ? fullProduct.images
    : fullProduct?.image?.url
    ? [fullProduct.image]
    : [];
  const findColorImageIndex = (colorEntry) => {
    if (!colorEntry || images.length === 0) return 0;
    const colorImageIndex = Number(colorEntry.imageIndex);
    if (
      Number.isInteger(colorImageIndex) &&
      colorImageIndex >= 0 &&
      colorImageIndex < images.length
    ) {
      return colorImageIndex;
    }
    if (colorEntry.imageUrl) {
      const matchedIndex = images.findIndex((img) => img?.url === colorEntry.imageUrl);
      if (matchedIndex >= 0) return matchedIndex;
    }
    return 0;
  };
  const safeIndex =
    images.length > 0 ? Math.min(Math.max(galleryIndex, 0), images.length - 1) : 0;
  const mainImage = images[safeIndex];
  const mainImageUrl = mainImage ? resolveUrl(mainImage.url) : "";
  const displayName = localizedStoreProductName(fullProduct, i18n.language);
  const mainImgAlt =
    (isStoreArabicLang(i18n.language)
      ? mainImage?.arAlt || mainImage?.alt
      : mainImage?.alt || mainImage?.arAlt) ||
    displayName ||
    t("store.checkout.productAlt", { defaultValue: "Product" });
  const localizedDesc = localizedStoreProductDescription(fullProduct, i18n.language);
  const description =
    localizedDesc ||
    t("store.productDescriptionFallback", {
      defaultValue:
        "A premium piece from our collection. Crafted with care for your space.",
    });
  const sku = fullProduct?.sku;
  const category = fullProduct?.category;
  const brand = fullProduct?.brand || category;
  const colors = fullProduct?.colors || [];
  const getSwatchBackground = (colorEntry) => {
    if (!colorEntry) return "#ccc";
    const hexesRaw = Array.isArray(colorEntry.hexes) ? colorEntry.hexes : [];
    const hexes = hexesRaw.map((h) => String(h || "").trim()).filter(Boolean);
    const fallback = String(colorEntry.hex || "").trim();
    const list = hexes.length > 0 ? hexes : fallback ? [fallback] : [];
    if (list.length <= 1) return list[0] || "#ccc";
    const step = 100 / list.length;
    const stops = list
      .map((hex, idx) => `${hex} ${Math.floor(idx * step)}% ${Math.floor((idx + 1) * step)}%`)
      .join(", ");
    return `conic-gradient(from 90deg, ${stops})`;
  };
  const detailsUrl = `/store/product/${fullProduct?._id || product?._id || ""}`;
  const outOfStock = isStoreProductOutOfStock(fullProduct);
  const stockCap = getStoreProductStockCap(fullProduct);

  const prevIcon = isRtl ? "fa-chevron-right" : "fa-chevron-left";
  const nextIcon = isRtl ? "fa-chevron-left" : "fa-chevron-right";

  const content = (
    <div
      className="store-quickview-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quickview-title"
      dir={isRtl ? "rtl" : "ltr"}
      lang={isRtl ? "ar" : "en"}
    >
      <div className="store-quickview-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="store-quickview-close"
          onClick={onClose}
          aria-label={t("store.quickViewClose", { defaultValue: "Close quick view" })}
        >
          <i className="fa-solid fa-times" />
        </button>

        {loading ? (
          <div className="store-quickview-loading">
            <div className="store-loading-spinner" />
            <p>{t("store.quickViewLoading", { defaultValue: "Loading…" })}</p>
          </div>
        ) : (
          <div className="store-quickview-body">
            <div className="store-quickview-gallery">
              {mainImageUrl ? (
                <>
                  <img
                    src={mainImageUrl}
                    alt={mainImgAlt}
                    style={{ objectFit: "contain", objectPosition: "center" }}
                  />
                  {images.length > 1 ? (
                    <>
                      <button
                        type="button"
                        className="store-quickview-gallery-arrow store-quickview-gallery-arrow--prev"
                        onClick={() =>
                          setGalleryIndex((i) =>
                            isRtl
                              ? i >= images.length - 1
                                ? 0
                                : i + 1
                              : i <= 0
                              ? images.length - 1
                              : i - 1
                          )
                        }
                        aria-label={t("store.productImagePrev", {
                          defaultValue: "Previous image",
                        })}
                      >
                        <i className={`fa-solid ${prevIcon}`} />
                      </button>
                      <button
                        type="button"
                        className="store-quickview-gallery-arrow store-quickview-gallery-arrow--next"
                        onClick={() =>
                          setGalleryIndex((i) =>
                            isRtl
                              ? i <= 0
                                ? images.length - 1
                                : i - 1
                              : i >= images.length - 1
                              ? 0
                              : i + 1
                          )
                        }
                        aria-label={t("store.productImageNext", {
                          defaultValue: "Next image",
                        })}
                      >
                        <i className={`fa-solid ${nextIcon}`} />
                      </button>
                    </>
                  ) : null}
                  <Link
                    to={detailsUrl}
                    className="store-quickview-view-details"
                    onClick={onClose}
                  >
                    {t("store.quickViewViewDetails", { defaultValue: "View details" })}
                  </Link>
                </>
              ) : (
                <div className="store-quickview-gallery-empty" aria-hidden />
              )}
            </div>

            <div className="store-quickview-info">
              <h2 id="quickview-title" className="store-quickview-title">
                {displayName}
              </h2>
              {brand ? (
                <p className="store-quickview-brand">
                  {translateStoreCategory(t, { label: brand }) || brand}
                </p>
              ) : null}
              {!outOfStock ? (
              <p className="store-quickview-price">
                <StoreProductPriceBlock
                  product={fullProduct || {}}
                  size="large"
                />
              </p>
              ) : null}
              <div className="store-quickview-description">
                <SimpleMarkdown text={description} />
              </div>

              {colors.length > 0 ? (
                <div className="store-quickview-colors">
                  <span className="store-quickview-colors-label">
                    {t("store.productColorLabel", { defaultValue: "Color:" })}
                  </span>
                  <div className="store-quickview-colors-list">
                    {colors.map((color) => (
                      <button
                        key={color.name}
                        type="button"
                        className={`store-quickview-color-swatch${
                          selectedColor === color.name ? " selected" : ""
                        }`}
                        data-color-name={color.name}
                        style={{ background: getSwatchBackground(color) }}
                        onClick={() => {
                          setSelectedColor(color.name);
                          setGalleryIndex(findColorImageIndex(color));
                        }}
                        aria-label={t("store.quickViewSelectColor", {
                          name: color.name,
                          defaultValue: `Select ${color.name}`,
                        })}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="store-quickview-qty-row">
                <div
                  className="store-quickview-qty"
                  aria-label={t("store.quickViewQty", { defaultValue: "Quantity" })}
                >
                  <button
                    type="button"
                    className="store-quickview-qty-btn"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    aria-label={t("store.quickViewDecreaseQty", {
                      defaultValue: "Decrease quantity",
                    })}
                  >
                    -
                  </button>
                  <span className="store-quickview-qty-value">{quantity}</span>
                  <button
                    type="button"
                    className="store-quickview-qty-btn"
                    onClick={() =>
                      setQuantity((q) =>
                        stockCap !== null ? Math.min(stockCap, q + 1) : q + 1
                      )
                    }
                    aria-label={t("store.quickViewIncreaseQty", {
                      defaultValue: "Increase quantity",
                    })}
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  className="store-quickview-add-cart"
                  onClick={handleAddToCart}
                  disabled={outOfStock}
                >
                  {outOfStock
                    ? t("store.outOfStock", { defaultValue: "Out of stock" })
                    : t("store.addToCart", { defaultValue: "Add to cart" })}
                </button>
              </div>

              {(sku || category) && (
                <div className="store-quickview-meta">
                  {sku ? (
                    <p className="store-quickview-sku">
                      <strong>{t("store.quickViewSku", { defaultValue: "SKU:" })}</strong>{" "}
                      <span className="store-product-serial-pill">{sku}</span>
                    </p>
                  ) : null}
                  {category ? (
                    <p className="store-quickview-category">
                      <strong>
                        {t("store.quickViewCategory", { defaultValue: "Category:" })}
                      </strong>{" "}
                      {translateStoreCategory(t, { label: category })}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export default QuickViewModal;
